import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Search, RefreshCw, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';

const EVENT_LABELS = {
  invoice: 'Invoice',
  payment_requested: 'Payment requested',
  payment_received: 'Payment received',
  ready_for_collection: 'Ready for collection',
  cancelled: 'Order cancelled',
  moq_not_met: 'MOQ not met',
  cart_reminder: 'Cart reminder',
  window_opening: 'Window opening',
  confirmed: 'Order confirmed',
};
const eventLabel = (e) =>
  EVENT_LABELS[e] || (e || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const RESENDABLE = new Set(['invoice', 'payment_received', 'ready_for_collection', 'cancelled', 'moq_not_met']);

const STATUS_CHIP = {
  read: 'text-teal border-teal/60 bg-teal/15',
  delivered: 'text-teal border-teal/40 bg-teal/10',
  sent: 'text-foreground/70 border-border bg-muted',
  failed: 'text-red-400 border-red-500/40 bg-red-500/10',
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', dateStyle: 'short', timeStyle: 'short' }) : '—';

const PAGE_SIZE = 50;

function SummaryCard({ label, value, sub, cls, testid }) {
  return (
    <div className={`rounded-2xl border p-4 ${cls}`} data-testid={testid}>
      <p className="font-body text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="font-heading text-3xl mt-1">{value}</p>
      {sub ? <p className="font-body text-xs opacity-70 mt-0.5">{sub}</p> : null}
    </div>
  );
}

export default function AdminComms() {
  const [channel, setChannel] = useState('whatsapp');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendId, setResendId] = useState('');

  const [status, setStatus] = useState('all');
  const [event, setEvent] = useState('all');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useCallback(() => {
    const p = { channel, page, page_size: PAGE_SIZE };
    if (status !== 'all') p.status = status;
    if (event !== 'all') p.event = event;
    if (q) p.q = q;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [channel, page, status, event, q, dateFrom, dateTo]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        adminAPI.listNotifications(params()),
        adminAPI.notificationsSummary({ channel, ...(q ? { q } : {}), ...(dateFrom ? { date_from: dateFrom } : {}), ...(dateTo ? { date_to: dateTo } : {}) }),
      ]);
      setRows(list.data?.rows || []);
      setTotal(list.data?.total || 0);
      setSummary(sum.data || null);
    } catch (e) {
      toast.error('Failed to load delivery log');
    } finally {
      setLoading(false);
    }
  }, [params, channel, q, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setPage(1); }, [channel, status, event, q, dateFrom, dateTo]);

  const resend = async (row) => {
    if (!RESENDABLE.has(row.event)) return;
    if (!window.confirm(`Resend the "${eventLabel(row.event)}" WhatsApp to ${row.contact_name || row.contact_phone}?`)) return;
    setResendId(`${row.order_id}:${row.at}`);
    try {
      const res = await adminAPI.sendWhatsAppTemplate(row.order_id, row.event);
      if (res.data?.success) { toast.success('WhatsApp resent'); fetchAll(); }
      else toast.error(res.data?.detail || 'WhatsApp failed to send');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    } finally {
      setResendId('');
    }
  };

  const clearFilters = () => {
    setStatus('all'); setEvent('all'); setQ(''); setQInput(''); setDateFrom(''); setDateTo('');
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = status !== 'all' || event !== 'all' || q || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="admin-comms">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-foreground flex items-center gap-2">
            <MessageCircle className="h-7 w-7 text-teal" /> WhatsApp Delivery
          </h1>
          <p className="font-body text-sm text-foreground/60 mt-1">
            Every message sent across all orders, with delivered &amp; read receipts.
          </p>
        </div>
        <Button
          data-testid="comms-refresh-btn"
          variant="outline"
          onClick={fetchAll}
          className="rounded-full border-border font-body text-sm gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Funnel summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="comms-funnel">
        <SummaryCard testid="funnel-sent" label="Sent" value={summary ? summary.sent + summary.delivered + summary.read : '…'} cls="border-border bg-card text-foreground" />
        <SummaryCard testid="funnel-delivered" label="Delivered" value={summary ? summary.delivered + summary.read : '…'} sub={summary ? `${summary.delivered_rate}%` : ''} cls="border-teal/30 bg-teal/5 text-foreground" />
        <SummaryCard testid="funnel-read" label="Read" value={summary ? summary.read : '…'} sub={summary ? `${summary.read_rate}% read rate` : ''} cls="border-teal/50 bg-teal/10 text-foreground" />
        <SummaryCard testid="funnel-failed" label="Failed" value={summary ? summary.failed : '…'} cls="border-red-500/30 bg-red-500/5 text-foreground" />
        <SummaryCard testid="funnel-total" label="Total" value={summary ? summary.total : '…'} cls="border-border bg-card text-foreground" />
      </div>

      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap" data-testid="comms-filters">
        <form
          onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }}
          className="relative flex-1 min-w-[220px]"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            data-testid="comms-search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search order #, name or phone…"
            className="pl-9 rounded-full bg-card border-border font-body text-sm"
          />
        </form>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="comms-status-filter" className="w-[150px] rounded-full bg-card border-border font-body text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger data-testid="comms-event-filter" className="w-[190px] rounded-full bg-card border-border font-body text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All message types</SelectItem>
            <SelectItem value="invoice">Invoice</SelectItem>
            <SelectItem value="payment_received">Payment received</SelectItem>
            <SelectItem value="ready_for_collection">Ready for collection</SelectItem>
            <SelectItem value="cancelled">Order cancelled</SelectItem>
            <SelectItem value="moq_not_met">MOQ not met</SelectItem>
            <SelectItem value="cart_reminder">Cart reminder</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input data-testid="comms-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] rounded-full bg-card border-border font-body text-sm" />
          <span className="text-foreground/40 text-sm">–</span>
          <Input data-testid="comms-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] rounded-full bg-card border-border font-body text-sm" />
        </div>
        {hasFilters && (
          <Button data-testid="comms-clear-filters" variant="ghost" onClick={clearFilters} className="rounded-full font-body text-sm gap-1 text-foreground/60">
            <X className="h-4 w-4" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-border" data-testid="comms-table-scroll">
        <table className="w-full" style={{ minWidth: '900px' }} data-testid="comms-table">
          <thead>
            <tr className="bg-card border-b border-border">
              {['Order', 'Customer', 'Phone', 'Message', 'Sent', 'Delivered', 'Read', 'Status', ''].map((h) => (
                <th key={h} className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-10 text-center text-foreground/50 font-body text-sm"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-10 text-center text-foreground/50 font-body text-sm" data-testid="comms-empty">No messages match these filters.</td></tr>
            ) : rows.map((row, i) => (
              <tr key={`${row.order_id}-${row.at}-${i}`} className="border-b border-border/50 last:border-0 hover:bg-muted/30" data-testid={`comms-row-${i}`}>
                <td className="p-4 font-body text-sm">
                  <Link to={`/admin/orders?order=${row.order_id}`} className="text-teal hover:underline font-medium" data-testid={`comms-order-link-${i}`}>
                    {row.order_number || `#${(row.order_id || '').slice(0, 8)}`}
                  </Link>
                </td>
                <td className="p-4 font-body text-sm text-foreground truncate max-w-[160px]">{row.contact_name || '—'}</td>
                <td className="p-4 font-body text-sm text-foreground/70">{row.contact_phone || '—'}</td>
                <td className="p-4 font-body text-sm text-foreground/80">{eventLabel(row.event)}</td>
                <td className="p-4 font-body text-xs text-foreground/60 whitespace-nowrap">{fmt(row.at)}</td>
                <td className="p-4 font-body text-xs text-foreground/60 whitespace-nowrap">{fmt(row.delivered_at)}</td>
                <td className="p-4 font-body text-xs text-foreground/60 whitespace-nowrap">{fmt(row.read_at)}</td>
                <td className="p-4">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 font-body text-[11px] font-semibold capitalize ${STATUS_CHIP[row.status] || STATUS_CHIP.sent}`} data-testid={`comms-status-${i}`}>
                    {row.status}
                  </span>
                </td>
                <td className="p-4 text-right">
                  {RESENDABLE.has(row.event) && (
                    <Button
                      data-testid={`comms-resend-${i}`}
                      size="sm"
                      variant="outline"
                      disabled={resendId === `${row.order_id}:${row.at}`}
                      onClick={() => resend(row)}
                      className="h-7 rounded-full border-teal/40 text-teal hover:bg-teal/10 font-body text-[11px] px-2.5 gap-1"
                    >
                      {resendId === `${row.order_id}:${row.at}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      Resend
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between" data-testid="comms-pagination">
        <p className="font-body text-sm text-foreground/50">{total} message{total === 1 ? '' : 's'}</p>
        <div className="flex items-center gap-2">
          <Button data-testid="comms-prev" variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-full border-border">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-body text-sm text-foreground/60">{page} / {totalPages}</span>
          <Button data-testid="comms-next" variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-full border-border">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
